import React, { useRef } from 'react'
import { router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import HistoryHeader from '@/components/history/HistoryTabs'
import HistoryFilters from '@/components/history/HistoryFilters'
import ChangeEntry from '@/components/history/ChangeEntry'
import { useUndo } from '@/components/history/UndoDialog'
import UnitScopeNote from '@/components/groups/UnitScopeNote'
import { DayCard, TimelineEmpty, TimelinePager, TimelineSkeleton, dayDelay, usePendingVisit } from '@/components/history/Timeline'
import type { HistoryChangesFilters, HistoryChangesProps } from '@/types/history'

/**
 * HISTORY, THE CHANGES TAB (docs/plans/2026-10-20-change-safety.md §5.12.3, §5.17.2, R2.6 to R2.10):
 * who changed which pricing rule on this hotel, when, and from what to what.
 *
 * The page answers "who changed what, when" in seconds: one card per hotel day, newest first, each
 * entry a person, a time and one sentence. Everything on it is composed by ResolvePricingHistoryMutation
 * and shown as sent: the day labels, the hotel-local times, the people (by email), the summaries,
 * the rows, the Undo state and every link. The page computes nothing.
 *
 * Filters are a filter change on this page, so they `router.visit` this tab's own path (the
 * backend-served basePath plus its fixed suffix) with the filters as query params, preserving state
 * and scroll. A filter change always starts from the newest entry: the keyset cursor is dropped.
 * Older and newest pages are the server's own hrefs.
 *
 * Open to everyone who can open the hotel, viewers included (R2.10). Only Undo needs more, and the
 * server decides it per entry (`undo.state`).
 */
const FILTER_PARAMS: (keyof HistoryChangesFilters)[] = ['person', 'area', 'from', 'to']

export default function Changes({
  currentUser,
  property,
  properties,
  tabs,
  activeTab,
  days,
  nextHref,
  newestHref,
  filters,
  filterOptions,
  emptyState,
  reportingScope,
}: HistoryChangesProps) {
  const path = `${property.basePath}/history/changes`
  const { pending, callbacks } = usePendingVisit()
  const listTop = useRef<HTMLDivElement>(null)
  // Undo reviews first, like every pricing save, then posts to the entry's own server href.
  const undo = useUndo()

  const filtered = FILTER_PARAMS.some((key) => filters[key])
  // A hotel with no history at all has nothing to filter: the bar would only be noise.
  const showFilters = days.length > 0 || filtered

  function visit(next: HistoryChangesFilters) {
    const data: Record<string, string> = {}
    FILTER_PARAMS.forEach((key) => {
      const value = next[key]
      if (value) data[key] = value
    })
    router.visit(path, { data, preserveState: true, preserveScroll: true, ...callbacks })
  }

  const applyFilters = (updates: Record<string, string | null>) => visit({ ...filters, ...updates })
  const clearFilters = () => visit({ person: null, area: null, from: null, to: null })

  return (
    <OperatorLayout title="History" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <HistoryHeader tabs={tabs} activeTab={activeTab} />

        {/* At a lead listing the history is the combined inventory's, as on the Prices tab: an entry
            recorded on a following listing names it. Renders nothing for a hotel on its own. */}
        <UnitScopeNote scope={reportingScope} covers="Every change logged here" listHotels className="animate-slide-in" />

        {showFilters && (
          <HistoryFilters
            idPrefix="history-changes"
            className="animate-slide-in"
            style={{ animationDelay: '100ms' }}
            selects={[
              { key: 'person', label: 'Person', allLabel: 'Everyone', value: filters.person, options: filterOptions.people },
              { key: 'area', label: 'Area', allLabel: 'All areas', value: filters.area, options: filterOptions.areas },
            ]}
            from={filters.from}
            to={filters.to}
            active={filtered}
            onChange={applyFilters}
            onClear={clearFilters}
          />
        )}

        <div ref={listTop} className="scroll-mt-24 space-y-4" aria-busy={pending}>
          {pending ? (
            <TimelineSkeleton />
          ) : emptyState ? (
            <TimelineEmpty emptyState={emptyState} onClear={filtered ? clearFilters : undefined} />
          ) : (
            days.map((day, index) => (
              <DayCard key={day.date} label={day.label} date={day.date} delay={dayDelay(index, 150)}>
                {day.entries.map((entry) => (
                  <ChangeEntry key={entry.id} entry={entry} onUndo={undo.start} />
                ))}
              </DayCard>
            ))
          )}
        </div>

        {!pending && (
          <TimelinePager
            nextHref={nextHref}
            newestHref={newestHref}
            olderLabel="Show older changes"
            callbacks={callbacks}
            listTop={listTop}
          />
        )}
      </div>

      {undo.dialog}
    </OperatorLayout>
  )
}
